import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import PlayerPicker from '../players/PlayerPicker';
import * as invitationsApi from '../../api/invitations';

/**
 * Pick people and invite them, from wherever you happen to be.
 *
 * Candidates come from the server already merged and ordered: the people in the
 * game or league you are looking at first, then the rest of your play history.
 * The first group starts ticked, which is what "based on the people here" means
 * — regrouping the same crowd is the common case and should be one tap.
 *
 * Anyone with an invitation still outstanding is shown but cannot be picked, so
 * a manager is not left wondering why a name they chose produced nothing.
 */
function InvitePeopleModal({ scope, scopeId, title, onClose, onInvited }) {
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);

  useEffect(() => {
    let live = true;
    invitationsApi.getCandidates(scope, scopeId)
      .then((rows) => {
        if (!live) return;
        const list = rows || [];
        setCandidates(list);
        setSelectedIds(list.filter((c) => c.inScope && !c.alreadyInvited).map((c) => c.playerId));
      })
      .catch(() => {
        if (live) setCandidates([]);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [scope, scopeId]);

  const toggle = (id) => {
    const candidate = candidates.find((c) => c.playerId === id);
    if (candidate?.alreadyInvited) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      setSent(await invitationsApi.invite(scope, scopeId, selectedIds));
      if (onInvited) onInvited();
    } catch (err) {
      setError(err.name === 'ApiError' ? err.body?.error || 'Could not send the invitations' : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Modal title="Invitations sent" onClose={onClose}>
        <div className="join-choice">
          <p>
            {sent.invited === 0
              ? 'Nobody new to invite — everyone you picked is already in or has already been asked.'
              : sent.invited === 1
                ? '1 invitation is on its way.'
                : `${sent.invited} invitations are on their way.`}
          </p>
          <p className="helper-text">
            They each choose whether to accept. Nobody is added until they do.
          </p>
          <button type="button" className="primary-btn full-width" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={title || 'Invite people'} onClose={onClose}>
      <div className="form-group">
        <label>Who to invite</label>
        {loading ? (
          <p className="helper-text">Finding people you play with...</p>
        ) : (
          <PlayerPicker
            players={candidates.map((c) => ({
              playerId: c.playerId,
              displayName: c.alreadyInvited ? `${c.displayName} (already asked)` : c.displayName,
              sharedGames: c.sharedGames,
            }))}
            selectedIds={selectedIds}
            onToggle={toggle}
            emptyHint="Once you've played some games, the people you play with most show up here."
          />
        )}
        <p className="helper-text">
          Everyone here is ticked to start with. They each get an invitation to accept
          or decline — nobody is added without agreeing.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <button
        type="button"
        className="primary-btn full-width"
        onClick={submit}
        disabled={busy || selectedIds.length === 0}
      >
        {busy
          ? 'Sending...'
          : selectedIds.length === 1
            ? 'Invite 1 person'
            : `Invite ${selectedIds.length} people`}
      </button>
    </Modal>
  );
}

export default InvitePeopleModal;
